import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { type ApolloCache } from "@apollo/client/cache";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useIntl } from "react-intl";
import { logApplicationFailure } from "@/lib/user-error";
import {
  AccessLevel,
  AttachProjectDocumentDocument,
  BulkAddCandidatesDocument,
  BulkRemoveCandidatesDocument,
  CreateSharedItemDocument,
  SharedItemType,
  DeleteMatchDocument,
  DeleteMatchResultDocument,
  DetachProjectDocumentDocument,
  GenerateTransientResultDocument,
  type GenerateTransientResultMutation,
  MatchCandidateListDocument,
  type MatchCandidateListQuery,
  MatchDetailDocument,
  type MatchDetailQuery,
  MatchDraftResultDocument,
  MatchDraftResultByIdDocument,
  MatchResultDetailDocument,
  type MatchResultDetailQuery,
  MatchResultListDocument,
  type MatchResultListQuery,
  PromoteMatchResultDocument,
  SortOrder,
  TakeOverSharedItemDocument,
  ToggleCandidateLockDocument,
  UpdateMatchDocument,
} from "@/graphql/generated/operations";
import { useMe } from "@/auth/use-me";
import {
  dataToDesign,
  matchDataPersistedEqual,
  matchToData,
  rebaseMatchData,
} from "./match-design-mapping";
import { useAgentStore } from "@/stores/agent-store";
import { EMPTY_MATCH_DATA, parseStepId } from "./match-design-rules";
import type { MatchData, StepId } from "./match-design-types";
import { MATCH_CANDIDATE_LIMIT } from "./match-constants";

export type MatchRole = "owner" | "admin" | "editor" | "reader";

// Map the four genesis access axes onto a single ordered role. Owner is the
// match creator (createdBy) and is the only role outside of the access table.
// Among non-owners we read the four axes as a ladder:
//   - prepare=WRITE         → admin (full powers incl. rename + access mgmt)
//   - any other WRITE       → editor (content edits, no rename / no access)
//   - all READ              → reader
// The 16-cell freedom of the matrix collapses into the four rungs that match
// the user-facing roles. See CONTEXT.md (target state) and @adr SPARK-0006-project-as-conceptual-entity@1
// discussion in conversation 2026-06-05.
function deriveMyRole(
  match: NonNullable<MatchDetailQuery["match"]> | null | undefined,
  myselfId: string | undefined,
): MatchRole | null {
  if (!match || !myselfId) return null;
  if (match.createdBy?.id === myselfId) return "owner";
  const row = match.access?.find((a) => a.person.id === myselfId);
  if (!row) return "reader";
  if (row.prepare === AccessLevel.Write) return "admin";
  if (
    row.design === AccessLevel.Write ||
    row.discover === AccessLevel.Write ||
    row.match === AccessLevel.Write
  ) {
    return "editor";
  }
  return "reader";
}

export type MatchCandidateNode = MatchCandidateListQuery["matchCandidates"]["nodes"][number];
export type SolverResult = NonNullable<GenerateTransientResultMutation["generateTransientMatchResult"]>;
export type MatchResultListItem = MatchResultListQuery["matchResults"]["nodes"][number];
export type MatchResultDetailNode = NonNullable<MatchResultDetailQuery["matchResult"]>;
export type MatchAccessRow = NonNullable<
  NonNullable<MatchDetailQuery["match"]>["access"]
>[number];
export type MatchAccessLevel = MatchAccessRow["prepare"];
export type LinkedSharedItem = NonNullable<MatchDetailQuery["match"]>["sharedItem"];
export type MatchOwner = NonNullable<MatchDetailQuery["match"]>["createdBy"];

export interface MatchAccessInput {
  personId: string;
  prepare: MatchAccessLevel;
  design: MatchAccessLevel;
  discover: MatchAccessLevel;
  match: MatchAccessLevel;
}

export type SaveState = "idle" | "pending" | "saving" | "error";

export interface UseMatchEditorResult {
  data: MatchData;
  stepId: StepId;
  setStepId: (s: StepId) => void;
  loading: boolean;
  saving: boolean;
  notFound: boolean;
  error: Error | null;
  saveState: SaveState;
  lastSavedAt: number | null;
  flush: () => Promise<void>;
  retrySave: () => void;
  update: (patch: Partial<MatchData>) => void;
  rename: (matchTitle: string) => void;
  saveSettings: (input: {
    matchTitle: string;
    matchDescription: string;
    access: MatchAccessInput[];
  }) => Promise<void>;
  discard: () => Promise<void>;

  access: MatchAccessRow[];
  sharedItem: LinkedSharedItem;
  owner: MatchOwner;
  myRole: MatchRole | null;
  candidates: MatchCandidateNode[];
  candidatesLoading: boolean;
  addCandidates: (personIds: string[]) => Promise<void>;
  removeCandidates: (candidateIds: string[]) => Promise<void>;
  toggleLock: (candidateId: string, locked: boolean) => Promise<void>;

  solverResult: SolverResult | null;
  solverResultSaved: boolean;
  hasUnsavedTransient: boolean;
  solverLoading: boolean;
  solverError: Error | null;
  runSolver: () => Promise<void>;
  clearSolverResult: () => void;
  assessOnly: boolean;
  setAssessOnly: (next: boolean) => void;

  results: MatchResultListItem[];
  resultsLoading: boolean;
  saveResult: (input: { name: string; notes?: string }) => Promise<void>;
  openResult: (id: string) => Promise<MatchResultDetailNode | null>;
  deleteResult: (id: string) => Promise<void>;

  // Verknüpfen / Veröffentlichen – see @adr SPARK-0006-project-as-conceptual-entity@1.
  // linkSharedItem points Match.sharedItem at an existing SharedItem; when
  // `copy` is true the genesis `takeOverSharedItem` mutation runs server-side
  // atomically (copy text fields into MatchDesign.project + set
  // Match.sharedItem). Per @adr SPARK-0006-project-as-conceptual-entity@1 no link timestamp is persisted.
  linkSharedItem: (input: { sharedItemId: string; copy: boolean }) => Promise<void>;
  unlinkSharedItem: () => Promise<void>;
  // Creates a new SharedItem from the current Project (overlap fields filled,
  // SharedItem-only fields left empty for the user to fill later) and points
  // Match.sharedItem at it. Returns the new SharedItem id.
  publishAsSharedItem: () => Promise<string>;

  // ProjectDocument lifecycle (see genesis MR !236 / #95). Sends inline file
  // content; genesis uploads to backdrop server-side and appends to
  // project.documents. Each call refetches the Match — projectFiles in
  // `data` resyncs from the server response. Outside the autosave envelope.
  attachProjectDocuments: (
    files: Array<{ fileName: string; mimeType: string; content: string }>,
  ) => Promise<void>;
  removeProjectDocument: (backdropFileId: string) => Promise<void>;
}

export interface UseMatchEditorOptions {
  onAfterDiscard?: () => void;
}

// createMatch/updateMatch return a discriminated union: the success variant, or
// ValidateMatchResult on validation rejection. Apollo doesn't throw on the latter
// (transport succeeded), so without this guard every match mutation path would
// silently swallow genesis-side rejections (access/person/input). Surface them as
// Error so the calling modal can show the actual message.
// `rejectedTypename` is the mutation's validation-rejection variant: "ValidateMatchResult" for the
// Match mutations, "ValidateMatchResultResult" for the MatchResult mutations (distinct __typenames).
export function throwIfValidationRejected(
  output: { __typename?: string; issues?: ReadonlyArray<{ message?: string | null } | null> | null } | null | undefined,
  rejectedTypename: string,
  fallbackMessage = "Genesis rejected the request.",
): void {
  if (!output) return;
  if (output.__typename === rejectedTypename) {
    const msg =
      (output.issues ?? [])
        .map((i) => i?.message)
        .filter((m): m is string => Boolean(m))
        .join("; ") || fallbackMessage;
    throw new Error(msg);
  }
}

// Shared translated fallback for `throwIfValidationRejected` across the match
// mutation call sites (editor, MatchPage, ShareItemDetailPage).
export function useValidationFallback(): string {
  const intl = useIntl();
  return intl.formatMessage({
    id: "match.validationRejected",
    defaultMessage: "Genesis rejected the request.",
  });
}

export function useMatchEditor(
  id: string,
  options: UseMatchEditorOptions = {},
): UseMatchEditorResult {
  const { onAfterDiscard } = options;
  const apollo = useApolloClient();
  const validationFallback = useValidationFallback();

  const matchQuery = useQuery(MatchDetailDocument, {
    variables: { id },
    fetchPolicy: "cache-and-network",
    // Genesis sometimes returns a non-fatal GraphQL error on the `access`
    // field while still emitting the rest of the Match. Default errorPolicy
    // "none" causes Apollo to drop the partial data, which surfaces as a
    // misleading "Match nicht gefunden" – `"all"` keeps the data and we
    // gracefully degrade the access list to empty (see line below).
    errorPolicy: "all",
  });

  const candidatesQuery = useQuery(MatchCandidateListDocument, {
    variables: { input: { first: MATCH_CANDIDATE_LIMIT, where: { matchId: { eq: id } } } },
    fetchPolicy: "cache-and-network",
  });

  const resultsQuery = useQuery(MatchResultListDocument, {
    variables: {
      input: {
        first: 50,
        // Saved-results history only: unsaved solve drafts (kept=false) are excluded so they never
        // show up as history entries (genesis persists a draft per solve; "Save" flips kept=true).
        where: { matchId: { eq: id }, kept: { eq: true } },
        order: { createdAt: SortOrder.Desc },
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const [updateMatch, updateState] = useMutation(UpdateMatchDocument);
  const [deleteMatch, deleteState] = useMutation(DeleteMatchDocument);
  const [createSharedItem] = useMutation(CreateSharedItemDocument);
  const [takeOverSharedItemMutation] = useMutation(TakeOverSharedItemDocument);
  const [bulkAdd] = useMutation(BulkAddCandidatesDocument);
  const [bulkRemove] = useMutation(BulkRemoveCandidatesDocument);
  const [toggleLockMutation] = useMutation(ToggleCandidateLockDocument);
  const [promoteResultMutation, saveResultState] = useMutation(PromoteMatchResultDocument);
  const [deleteResultMutation] = useMutation(DeleteMatchResultDocument);
  const [attachProjectDocumentMutation] = useMutation(AttachProjectDocumentDocument);
  const [detachProjectDocumentMutation] = useMutation(DetachProjectDocumentDocument);
  // genesis#168: the solve is a mutation (it persists a draft) — mutations always hit the network
  // and never populate the watch-query cache, so the old useLazyQuery fetchPolicy pinning that
  // forced a fresh request each click is no longer needed.
  const [runSolverQuery, solverState] = useMutation(GenerateTransientResultDocument, {
    // Same rationale as MatchDetailDocument above: genesis intermittently
    // returns partial errors on transient results; keep the data so the
    // result panel can render whatever the solver did produce.
    errorPolicy: "all",
  });

  // stepId lives in the URL (?step=people|result) so that browser back to a
  // match detail page restores the step the user was on, not the default
  // "challenge". Step changes use replace: true — the stepper has its own
  // prev/next controls, so clicking through it shouldn't flood history.
  const [stepSearchParams, setStepSearchParams] = useSearchParams();
  const stepId = parseStepId(stepSearchParams.get("step"));
  const setStepId = useCallback(
    (next: StepId) => {
      setStepSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "challenge") params.delete("step");
          else params.set("step", next);
          return params;
        },
        { replace: true },
      );
    },
    [setStepSearchParams],
  );
  // The solve result is a genesis-persisted DRAFT (kept=false), not browser state: a solve returns
  // it (with its id), the agent can create it too, and "Save" promotes the SAME row to kept=true.
  // We hold it in memory for the panel to render; `draftId` targets the row for save/discard. It is
  // intentionally NOT restored across a fresh mount (discard-on-open, ADR): a crash-orphan draft is
  // invisible (excluded from history, never auto-loaded) and the next solve's server-side replace
  // reclaims it — so there is no sessionStorage and no client GC.
  const [solverResult, setSolverResult] = useState<SolverResult | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [solverResultSaved, setSolverResultSaved] = useState(false);
  const [assessOnly, setAssessOnly] = useState(false);
  // Bumped on every solver-input invalidation (clearSolverResult). runSolver
  // captures it before issuing the request and discards the response if it
  // changed while the solve was in flight — otherwise an edit made mid-solve is
  // clobbered by the older solve's response landing after the clear.
  const solverEpoch = useRef(0);

  // Server-derived data – the truth at last save.
  const serverData: MatchData = matchQuery.data?.match
    ? matchToData(matchQuery.data.match)
    : EMPTY_MATCH_DATA;

  // Local editing overlay. When non-null, it shadows serverData so the user
  // sees their in-flight edits without waiting for the autosave round-trip.
  // We reset it to null after a successful save acknowledges the latest patch.
  const [pending, setPending] = useState<MatchData | null>(null);
  const data: MatchData = pending ?? serverData;
  const dataRef = useRef<MatchData>(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  // Stable handle to the latest matchQuery for the agent-write reconcile effect below, which must
  // depend ONLY on the epoch (matchQuery gets a new identity every render, which would otherwise
  // cancel the in-flight reconcile on the very next render).
  const matchQueryRef = useRef(matchQuery);
  useEffect(() => {
    matchQueryRef.current = matchQuery;
  });

  // Autosave plumbing (@adr SPARK-0008-match-editor-autosave@1). A change to MatchData schedules a debounced
  // updateMatch ~1s after the last change; flush() awaits any pending save +
  // any pending debounce. runSolver awaits flush() so it always reads a
  // consistent design.
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const lastSavedSnapshot = useRef<MatchData | null>(null);

  const performSave = useCallback(
    async (next: MatchData) => {
      const promise = (async () => {
        setSaveState("saving");
        try {
          // Diff against the last saved snapshot and only send the top-level
          // fields that actually changed. Genesis gates `name`/`description`
          // via the `prepare` access axis and `design` via the `design` axis
          // (MatchController.java:907-924); re-sending unchanged prepare-gated
          // fields on every design edit would 403 an editor (design=WRITE,
          // prepare=READ). When no baseline exists yet (e.g. save fired before
          // the initial query resolved) fall through to the full payload —
          // identical to legacy behaviour.
          const baseline = lastSavedSnapshot.current;
          const nextName = next.matchTitle || null;
          const nextDescription = next.matchDescription || null;
          const nextDesign = dataToDesign(next);
          const input: {
            name?: string | null;
            description?: string | null;
            design?: Record<string, unknown>;
          } = {};
          if (!baseline || (baseline.matchTitle || null) !== nextName) {
            input.name = nextName;
          }
          if (!baseline || (baseline.matchDescription || null) !== nextDescription) {
            input.description = nextDescription;
          }
          if (
            !baseline ||
            JSON.stringify(dataToDesign(baseline)) !== JSON.stringify(nextDesign)
          ) {
            input.design = nextDesign;
          }
          if (Object.keys(input).length === 0) {
            setSaveState("idle");
            return;
          }
          await updateMatch({
            variables: { id, input: { data: input } },
          });
          lastSavedSnapshot.current = next;
          setLastSavedAt(Date.now());
          // Don't reset pending here: dataToDesign drops incomplete rows
          // (empty UUID id), so server-roundtripped serverData would lack
          // them and the user-visible row would disappear mid-edit. Pending
          // stays sticky and is only cleared on explicit external refetch
          // (e.g. after generateAIRequirements) or on first load.
          setSaveState("idle");
        } catch (err) {
          setSaveState("error");
          throw err;
        }
      })();
      inFlight.current = promise;
      try {
        await promise;
      } finally {
        if (inFlight.current === promise) inFlight.current = null;
      }
    },
    [id, updateMatch],
  );

  // In-flight ProjectDocument attach/detach promise. attach/detach happen
  // outside the autosave envelope (see attachProjectDocuments / removeProjectDocument
  // below); public flush() awaits this so generation triggers see the latest
  // project.documents. Internal flushAutosave() does NOT await it — attach/
  // detach would otherwise deadlock on their own promise.
  const inFlightProjectDocs = useRef<Promise<void> | null>(null);

  const flushAutosave = useCallback(async (): Promise<void> => {
    // Serialise: a rapid edit can queue a new debounce while a prior save is
    // still in-flight. Awaiting inFlight first prevents the new performSave
    // from overwriting inFlight.current and orphaning the older write (which
    // can land after the newer one and clobber it).
    if (inFlight.current) {
      await inFlight.current;
    }
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
      const snapshot = dataRef.current;
      await performSave(snapshot);
    }
  }, [performSave]);

  // Re-fire the save the user asked to retry after a failed autosave (@adr SPARK-0008-match-editor-autosave@1
  // "Erneut versuchen"). Local edit state is preserved on failure, so this just
  // re-runs performSave against the current design.
  const retrySave = useCallback(() => {
    void performSave(dataRef.current);
  }, [performSave]);

  const flush = useCallback(async (): Promise<void> => {
    await flushAutosave();
    if (inFlightProjectDocs.current) {
      await inFlightProjectDocs.current;
    }
  }, [flushAutosave]);

  // Server-truth seam. Every path that writes a field genesis owns (the
  // Match name/description, sharedItem link, access list, AI-rewritten
  // design) must (1) flush any in-flight autosave so the design we just
  // overwrote isn't stomped back by the debounce, (2) run the actual
  // server write, (3) refetch the match so cache + serverData advance,
  // (4) drop the local `pending` overlay so it stops shadowing the new
  // serverData, (5) restamp the saved snapshot + state so the
  // "Gespeichert vor X Sekunden" indicator stays honest. Forgetting any
  // of these in any one callback is the bug class that produced the
  // stale match-name after settings-save — keep the protocol in one
  // place so adding a sixth caller is two lines, not a checklist.
  const commitServerTruth = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      await flush();
      const result = await fn();
      await matchQuery.refetch();
      setPending(null);
      lastSavedSnapshot.current = dataRef.current;
      setLastSavedAt(Date.now());
      setSaveState("idle");
      return result;
    },
    [flush, matchQuery],
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // Seed the diff baseline from the first server snapshot so performSave can
  // tell which top-level fields the user actually touched. commitServerTruth
  // and performSave keep it in sync after that.
  useEffect(() => {
    if (lastSavedSnapshot.current === null && matchQuery.data?.match) {
      lastSavedSnapshot.current = matchToData(matchQuery.data.match);
    }
  }, [matchQuery.data]);

  // Reconcile with an Assistant design-write to this match (agent-store epoch bump on turn end). The
  // write is already persisted server-side; adopt it locally without letting the editor's stale
  // whole-blob autosave clobber it: cancel any queued/in-flight save, refetch, then rebase any unsaved
  // local edits onto the new server truth (their edits win per-field; everything else adopts server —
  // which now carries the agent's change). Mirrors the commitServerTruth protocol for external writes.
  const agentWriteEpoch = useAgentStore((s) => s.agentWriteEpoch);
  const reconciledEpoch = useRef(agentWriteEpoch);
  useEffect(() => {
    if (agentWriteEpoch === reconciledEpoch.current) return;
    reconciledEpoch.current = agentWriteEpoch;
    let cancelled = false;
    void (async () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      if (inFlight.current) await inFlight.current;
      const res = await matchQueryRef.current.refetch();
      if (cancelled) return;
      const server = res.data?.match ? matchToData(res.data.match) : null;
      if (!server) return;
      const baseline = lastSavedSnapshot.current;
      const rebased = baseline ? rebaseMatchData(baseline, dataRef.current, server) : server;
      setPending(matchDataPersistedEqual(rebased, server) ? null : rebased);
      lastSavedSnapshot.current = server;
      setLastSavedAt(Date.now());
      setSaveState("idle");
    })();
    return () => {
      cancelled = true;
    };
  }, [agentWriteEpoch]);

  const access: MatchAccessRow[] = matchQuery.data?.match?.access ?? [];
  const sharedItem: LinkedSharedItem = matchQuery.data?.match?.sharedItem ?? null;
  const owner: MatchOwner = matchQuery.data?.match?.createdBy ?? null;
  const me = useMe();
  const myRole: MatchRole | null = useMemo(
    () => deriveMyRole(matchQuery.data?.match, me.id),
    [matchQuery.data?.match, me.id],
  );
  const candidates = candidatesQuery.data?.matchCandidates.nodes ?? [];
  const results = resultsQuery.data?.matchResults.nodes ?? [];

  const candidateListRefetch = useCallback(
    () => candidatesQuery.refetch(),
    [candidatesQuery],
  );
  const resultListRefetch = useCallback(() => resultsQuery.refetch(), [resultsQuery]);

  // A solve draft is only valid for the design, candidate set and mode it was computed against, so
  // drop it whenever any solver input changes rather than let a stale team render as current or get
  // saved with a mismatched design (@adr SPARK-0008-match-editor-autosave@1). The draft is a genesis row, so also delete it there
  // (best-effort, unless already promoted to a kept result) — else it would linger until the next
  // solve replaces it. Over-clearing is safe: the cost is a re-solve; a stale result shown as
  // current is a correctness bug.
  const clearSolverResult = useCallback(() => {
    solverEpoch.current += 1;
    setSolverResult(null);
    setDraftId(null);
    setSolverResultSaved(false);
    if (draftId && !solverResultSaved) {
      void deleteResultMutation({ variables: { input: { id: draftId } } }).catch(() => {
        // Best-effort: a lingering draft is invisible (excluded from history) and the next solve
        // replaces it server-side, so a failed delete never blocks the workflow.
      });
    }
  }, [draftId, solverResultSaved, deleteResultMutation]);

  // Unmount hygiene (#227 part 1): leaving the editor with an unsaved draft still in play — via any
  // exit the navigation guard didn't route through discard (e.g. a programmatic redirect) — must not
  // leave an orphaned kept=false row on the server. Ref-based so it fires once on unmount with the
  // latest ids and never re-runs on every draft change. Safe w.r.t. the agent: it only deletes the
  // draft THIS editor holds unsaved, which the user has necessarily abandoned by navigating away —
  // an agent draft the user still needs lives in the match they're still mounted on.
  const draftIdRef = useRef(draftId);
  const solverResultSavedRef = useRef(solverResultSaved);
  useEffect(() => {
    draftIdRef.current = draftId;
    solverResultSavedRef.current = solverResultSaved;
  });
  useEffect(
    () => () => {
      if (draftIdRef.current && !solverResultSavedRef.current) {
        void deleteResultMutation({ variables: { input: { id: draftIdRef.current } } }).catch(() => {});
      }
    },
    [deleteResultMutation],
  );

  const scheduleSave = useCallback(
    (next: MatchData) => {
      setPending(next);
      setSaveState("pending");
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        void performSave(next);
      }, 1000);
    },
    [performSave],
  );

  const update = useCallback(
    (patch: Partial<MatchData>) => {
      clearSolverResult();
      scheduleSave({ ...dataRef.current, ...patch });
    },
    [clearSolverResult, scheduleSave],
  );

  // Rename is a prepare-gated label change that doesn't affect the solve, so
  // (unlike update) it must NOT clear the solver result.
  const rename = useCallback(
    (matchTitle: string) => {
      scheduleSave({ ...dataRef.current, matchTitle });
    },
    [scheduleSave],
  );

  const saveSettings = useCallback(
    async ({
      matchTitle,
      matchDescription,
      access,
    }: {
      matchTitle: string;
      matchDescription: string;
      access: MatchAccessInput[];
    }) => {
      await commitServerTruth(async () => {
        await updateMatch({
          variables: {
            id,
            input: {
              data: {
                name: matchTitle.trim() || null,
                description: matchDescription.trim() || null,
                access: access.map((a) => ({
                  person: { id: a.personId },
                  prepare: a.prepare,
                  design: a.design,
                  discover: a.discover,
                  match: a.match,
                })),
              },
            },
          },
        });
      });
    },
    [commitServerTruth, id, updateMatch],
  );

  const discard = useCallback(async () => {
    await deleteMatch({
      variables: { input: { id } },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "Match", id }) });
        cache.gc();
      },
    });
    onAfterDiscard?.();
  }, [deleteMatch, id, onAfterDiscard]);

  // The bulk-candidate mutations return only { match: UUID } /
  // { candidatesRemoved: [UUID] } at the schema level, so Apollo's
  // normalized cache can't auto-update the parent Match.candidateCount
  // that MatchList renders. Adjust the cached count in `update` so the
  // /match list row stays honest the moment the user navigates back.
  const adjustCandidateCount = useCallback(
    (delta: number) => (cache: ApolloCache) => {
      cache.modify({
        id: cache.identify({ __typename: "Match", id }),
        fields: {
          candidateCount(current: number = 0) {
            return Math.max(0, current + delta);
          },
        },
      });
    },
    [id],
  );

  const addCandidates = useCallback(
    async (personIds: string[]) => {
      if (personIds.length === 0) return;
      clearSolverResult();
      await bulkAdd({
        variables: {
          input: {
            match: { id },
            data: personIds.map((personId) => ({ person: { id: personId }, locked: false })),
          },
        },
        update: adjustCandidateCount(personIds.length),
      });
      await candidateListRefetch();
    },
    [adjustCandidateCount, bulkAdd, candidateListRefetch, clearSolverResult, id],
  );

  const removeCandidates = useCallback(
    async (candidateIds: string[]) => {
      if (candidateIds.length === 0) return;
      clearSolverResult();
      await bulkRemove({
        variables: { input: { match: id, candidates: candidateIds } },
        update: adjustCandidateCount(-candidateIds.length),
      });
      await candidateListRefetch();
    },
    [adjustCandidateCount, bulkRemove, candidateListRefetch, clearSolverResult, id],
  );

  const toggleLock = useCallback(
    async (candidateId: string, locked: boolean) => {
      clearSolverResult();
      await toggleLockMutation({
        variables: { id: candidateId, input: { data: { locked } } },
        optimisticResponse: {
          __typename: "Mutation",
          updateMatchCandidate: {
            __typename: "UpdateMatchCandidateResult",
            result: {
              __typename: "MatchCandidate",
              id: candidateId,
              locked,
            },
          },
        },
      });
    },
    [clearSolverResult, toggleLockMutation],
  );

  const runSolver = useCallback(async () => {
    const epoch = solverEpoch.current;
    await flush();
    // An edit landed during flush()'s autosave await and bumped the epoch — the design/mode/
    // candidate set has moved past what this solve would compute. Bail before sending it, else the
    // solve persists a stale server-side draft the UI would only discard afterward (checked again
    // post-response below for edits that land while the solve itself is in flight).
    if (solverEpoch.current !== epoch) return;
    // assessOnly is no longer sent (genesis #159): genesis derives it from the
    // immutable Match.matchMode. The local flag stays — it drives UI (button
    // label, locked-seat bypass).
    const res = await runSolverQuery({
      variables: { input: { matchId: id } },
    });
    const next = res.data?.generateTransientMatchResult ?? null;
    // Inputs changed while this solve was in flight — its response is for a design/mode/candidate set
    // the user has already moved past. Genesis has ALREADY persisted this response as the match's
    // unsaved draft (replaceDraft), computed against the superseded inputs; the UI drops it, but
    // GetMatchDraft/loadDraftResult would still surface a stale draft. So delete the orphan
    // server-side before bailing (fire-and-forget; the next solve's replaceDraft would also
    // overwrite it, but the assistant could read it in the meantime).
    if (solverEpoch.current !== epoch) {
      if (next?.id) {
        void deleteResultMutation({ variables: { input: { id: next.id } } }).catch(() => {});
      }
      return;
    }
    // The solve persisted this as a draft (kept=false); hold its id so Save can promote it and a
    // requirement-edit can delete it.
    setSolverResult(next);
    setDraftId(next?.id ?? null);
    setSolverResultSaved(false);
  }, [flush, id, runSolverQuery, deleteResultMutation]);

  // Load the match's current unsaved draft (kept=false) — used when the Assistant ran the solve, so
  // the UI shows exactly that one persisted result without re-solving (agent-refresh signal below).
  const loadDraftResult = useCallback(async () => {
    // Two-step: the list query yields only the draft's id (its `teams` is never populated by the
    // matchResults resolver — selecting it there throws and fails the load); then matchResult(id)
    // fetches the full roster, the only path that populates `teams`.
    const list = await apollo.query({
      query: MatchDraftResultDocument,
      variables: { input: { first: 1, where: { matchId: { eq: id }, kept: { eq: false } } } },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });
    const draftResultId = list.data?.matchResults.nodes[0]?.id ?? null;
    if (!draftResultId) return;
    const detail = await apollo.query({
      query: MatchDraftResultByIdDocument,
      variables: { id: draftResultId },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });
    const node = detail.data?.matchResult ?? null;
    if (!node) return;
    // Same SolverDraftResult fragment as GenerateTransientResult's payload, so no cast needed.
    setSolverResult(node);
    setDraftId(node.id);
    setSolverResultSaved(false);
  }, [apollo, id]);

  const saveResult = useCallback(
    async ({ name, notes }: { name: string; notes?: string }) => {
      // "Save" promotes the persisted draft (kept=false) to a kept result — the row already exists
      // from the solve, so this is an update (kept=true + name/notes), not a create.
      if (!draftId) return;
      const res = await promoteResultMutation({
        variables: {
          id: draftId,
          input: { data: { kept: true, name, notes: notes || null } },
        },
      });
      throwIfValidationRejected(res.data?.updateMatchResult, "ValidateMatchResultResult", validationFallback);
      await resultListRefetch();
      setSolverResultSaved(true);
    },
    [draftId, promoteResultMutation, resultListRefetch, validationFallback],
  );

  // React to an Assistant-run solve for THIS match (agent-store epoch bump on turn end): the solve
  // persisted a draft server-side, so switch to the Result step and load it — no re-solve. Mirrors
  // the agentWriteEpoch reconcile; loadDraftResult is a plain read, so a concurrent edit that
  // cleared the draft simply finds nothing to load. The signal is matchId-scoped (#3): a solve
  // bound to another match (the user navigated mid-turn) bumps the epoch but carries that match's
  // id, so this editor ignores it and only reacts to its own solve.
  const matchSolveEpoch = useAgentStore((s) => s.matchSolveEpoch);
  const matchSolveMatchId = useAgentStore((s) => s.matchSolveMatchId);
  const reactedSolveEpoch = useRef(matchSolveEpoch);
  useEffect(() => {
    if (matchSolveEpoch === reactedSolveEpoch.current) return;
    reactedSolveEpoch.current = matchSolveEpoch;
    if (matchSolveMatchId !== id) return;
    setStepId("result");
    void loadDraftResult();
  }, [matchSolveEpoch, matchSolveMatchId, id, setStepId, loadDraftResult]);

  const openResult = useCallback(
    async (resultId: string): Promise<MatchResultDetailNode | null> => {
      try {
        const res = await apollo.query({
          query: MatchResultDetailDocument,
          variables: { id: resultId },
          fetchPolicy: "network-only",
          errorPolicy: "all",
        });
        // Apollo v4 packs GraphQL errors inside a single `error` field rather
        // than exposing a plural `errors` array — unwrap it for parity.
        if (res.error && CombinedGraphQLErrors.is(res.error)) {
          logApplicationFailure("match.open-result-partial", { level: "warn" });
        } else if (res.error) {
          logApplicationFailure("match.open-result-error", { level: "warn" });
        }
        return res.data?.matchResult ?? null;
      } catch {
        logApplicationFailure("match.open-result");
        return null;
      }
    },
    [apollo],
  );

  const deleteResult = useCallback(
    async (resultId: string) => {
      await deleteResultMutation({
        variables: { input: { id: resultId } },
        update(cache) {
          cache.evict({ id: cache.identify({ __typename: "MatchResult", id: resultId }) });
          cache.gc();
        },
      });
      await resultListRefetch();
    },
    [deleteResultMutation, resultListRefetch],
  );

  // ProjectDocument lifecycle (genesis MR !236). Outside the autosave envelope:
  // each call is its own mutation that bumps Match.version. We can't reuse
  // commitServerTruth here because its flush() awaits inFlightProjectDocs, which
  // would deadlock on our own promise. Instead: flush autosave only, run the
  // mutation, refetch, drop pending — and register the promise so public
  // flush() (used by runSolver) awaits it.
  const runProjectDocumentMutation = useCallback(
    async (fn: () => Promise<void>): Promise<void> => {
      await flushAutosave();
      await fn();
      await matchQuery.refetch();
      setPending(null);
      lastSavedSnapshot.current = dataRef.current;
      setLastSavedAt(Date.now());
      setSaveState("idle");
    },
    [flushAutosave, matchQuery],
  );

  const attachProjectDocuments = useCallback(
    async (files: Array<{ fileName: string; mimeType: string; content: string }>) => {
      if (files.length === 0) return;
      const promise = runProjectDocumentMutation(async () => {
        await attachProjectDocumentMutation({
          variables: { input: { matchId: id, files } },
        });
      });
      inFlightProjectDocs.current = promise;
      try {
        await promise;
      } finally {
        if (inFlightProjectDocs.current === promise) inFlightProjectDocs.current = null;
      }
    },
    [attachProjectDocumentMutation, id, runProjectDocumentMutation],
  );

  const removeProjectDocument = useCallback(
    async (backdropFileId: string) => {
      const promise = runProjectDocumentMutation(async () => {
        await detachProjectDocumentMutation({
          variables: { input: { matchId: id, backdropFileId } },
        });
      });
      inFlightProjectDocs.current = promise;
      try {
        await promise;
      } finally {
        if (inFlightProjectDocs.current === promise) inFlightProjectDocs.current = null;
      }
    },
    [detachProjectDocumentMutation, id, runProjectDocumentMutation],
  );

  const linkSharedItem = useCallback(
    async ({ sharedItemId, copy }: { sharedItemId: string; copy: boolean }) => {
      await commitServerTruth(async () => {
        if (copy) {
          // Übernehmen: atomic copy + link on genesis side. Server reads
          // the SharedItem with the caller's token, copies name/short-
          // description/description into MatchDesign.project, and sets
          // Match.sharedItem. Documents are not copied in v1 (backdrop
          // server-side copy deferred).
          await takeOverSharedItemMutation({
            variables: { input: { matchId: id, sharedItemId } },
          });
        } else {
          // Nur verknüpfen: just set the FK, copy nothing.
          const res = await updateMatch({
            variables: {
              id,
              input: { data: { sharedItem: { id: sharedItemId } } },
            },
          });
          throwIfValidationRejected(res.data?.updateMatch, "ValidateMatchResult", validationFallback);
        }
      });
    },
    [commitServerTruth, id, takeOverSharedItemMutation, updateMatch, validationFallback],
  );

  const unlinkSharedItem = useCallback(async () => {
    await commitServerTruth(async () => {
      const res = await updateMatch({
        variables: {
          id,
          input: {
            data: {
              // Explicit null → genesis clears Match.sharedItem (verified
              // against MatchController.updateRecord: rel == null →
              // setSharedItemId(null)). Per @adr SPARK-0006-project-as-conceptual-entity@1: copied project.*
              // stays intact – only the link drops.
              sharedItem: null,
            },
          },
        },
      });
      throwIfValidationRejected(res.data?.updateMatch, "ValidateMatchResult", validationFallback);
    });
  }, [commitServerTruth, id, updateMatch, validationFallback]);

  const publishAsSharedItem = useCallback(async (): Promise<string> => {
    return commitServerTruth(async () => {
      const base = dataRef.current;
      if (
        !base.projectName.trim() &&
        !base.projectShortDescription.trim() &&
        !base.projectDescription.trim()
      ) {
        throw new Error("Projekt braucht mindestens Titel, Kurzbeschreibung oder Beschreibung.");
      }
      // SharedItem-only fields (mainImage, sharedWith, colors) are left
      // empty by design – @adr SPARK-0006-project-as-conceptual-entity@1 promotes them to a follow-up step the
      // user completes via the SharedItem editor.
      const createResult = await createSharedItem({
        variables: {
          input: {
            data: {
              itemType: SharedItemType.Project,
              name: base.projectName,
              shortDescription: base.projectShortDescription || null,
              description: base.projectDescription || null,
            },
          },
        },
        update(cache) {
          // CreateSharedItem returns only { id, name } so the new entity
          // can't be slotted into any cached SharedItems list. Evict the
          // list field so /share + LinkableSharedItems refetch it the next
          // time they mount.
          cache.evict({ fieldName: "sharedItems" });
          cache.gc();
        },
      });
      const output = createResult.data?.createSharedItem;
      if (output?.__typename === "ValidateSharedItemResult") {
        throw new Error(
          output.issues?.map((i) => i?.message).filter(Boolean).join(", ") ||
            "SharedItem konnte nicht erstellt werden.",
        );
      }
      const newId = output?.__typename === "CreateSharedItemResult" ? output.result?.id : null;
      if (!newId) {
        throw new Error("SharedItem konnte nicht erstellt werden.");
      }
      const updateRes = await updateMatch({
        variables: {
          id,
          input: { data: { sharedItem: { id: newId } } },
        },
      });
      throwIfValidationRejected(updateRes.data?.updateMatch, "ValidateMatchResult", validationFallback);
      return newId;
    });
  }, [commitServerTruth, createSharedItem, id, updateMatch, validationFallback]);

  const error = useMemo<Error | null>(
    () =>
      (matchQuery.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      (solverState.error as Error | undefined) ??
      (saveResultState.error as Error | undefined) ??
      null,
    [
      matchQuery.error,
      updateState.error,
      deleteState.error,
      solverState.error,
      saveResultState.error,
    ],
  );

  return {
    data,
    stepId,
    setStepId,
    loading: matchQuery.loading && !matchQuery.data,
    saving: updateState.loading || deleteState.loading,
    notFound: !matchQuery.loading && !matchQuery.data?.match,
    error,
    saveState,
    lastSavedAt,
    flush,
    retrySave,
    update,
    rename,
    saveSettings,
    discard,

    access,
    sharedItem,
    owner,
    myRole,
    candidates,
    candidatesLoading: candidatesQuery.loading && !candidatesQuery.data,
    addCandidates,
    removeCandidates,
    toggleLock,

    solverResult,
    solverResultSaved,
    // A genesis draft (kept=false) exists and hasn't been promoted to a saved result — the exit guard
    // and unmount cleanup key off this (#227).
    hasUnsavedTransient: Boolean(draftId) && !solverResultSaved,
    solverLoading: solverState.loading,
    solverError: (solverState.error as Error | undefined) ?? null,
    runSolver,
    clearSolverResult,
    assessOnly,
    setAssessOnly,

    results,
    resultsLoading: resultsQuery.loading && !resultsQuery.data,
    saveResult,
    openResult,
    deleteResult,

    linkSharedItem,
    unlinkSharedItem,
    publishAsSharedItem,

    attachProjectDocuments,
    removeProjectDocument,
  };
}
